/*
 * Some declarations below are deliberately unused: they belong to UI that is commented out in place
 * (the job search form, the header, the filter and sort bars). Deleting them would delete work in
 * progress, and whether that UI ships is a product decision rather than a lint one. Each one carries
 * its own `eslint-disable-next-line`, so the rule still catches anything new that goes unused here.
 */
/**
 * Job search results page with Visual Theme - migrated from EJS
 * Visual theme: Magazine-style layout with stunning photography and elegant typography
 *
 * URL: /[language]/trouver-un-emploi/offres(/de-:position)?(/a-:town)?(/de-:position)?
 * URL: /[language]/find-a-job/offers(/de-:position)?(/a-:town)?(/de-:position)?
 * URL: /[language]/encontrar-un-empleo/ofertas(/de-:position)?(/a-:town)?(/de-:position)?
 *
 * @module /jobs
 */

import { ParsedUrlQuery } from 'querystring';
import { Position } from '@bruce.work/bruce-doc-class-typescript/doc/Class/Position';
import { Publication, PublicationStatus, PublicationTarget } from '@bruce.work/bruce-doc-class-typescript/doc/Class/Publication';
import { Editable, Recorded } from '@bruce.work/bruce-doc-event-typescript/doc/Class';
import { Piece } from '@bruce.work/bruce-doc-event-typescript/doc/Event/Query/build';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import React, { useCallback, useState } from 'react';
import { FiBriefcase, FiCalendar, FiMapPin, FiZap } from 'react-icons/fi';
import styled from 'styled-components';
import { PAGE_TYPE, PageBasicParams, PageBasicProps } from '../../interfaces';
import nextI18NextConfig from '../../next-i18next.config.js';
import { ICONS } from '../../public/img/icons';
import { fetchFooterData, fetchNavbarData } from '../../utilities/cms';
import { cleanHtmlToText, formatTimeAgo, getJobImageUrl, parseUnderlineFromString, renderUnderline } from '../../utilities/functions';

// Server-side only import (only used in getServerSideProps)
import Button_v2 from '@/components/molecules/Button/index';
import Title_v2 from '@/components/molecules/Title/index';
import PageWrapper from '@/components/wrappers/PageWrapper';
import { startServerSide } from '@/parse/server';
import { FooterProps, NavbarProps } from '@/queries/components/blocks';
import devices from '@/utilities/devices';

/**
 * Generate URL-friendly slug from publication title
 */
const generateSlug = (title: string): string => {
	return title
		.toLowerCase()
		.trim()
		.replace(/[àáâãäå]/g, 'a')
		.replace(/[èéêë]/g, 'e')
		.replace(/[ìíîï]/g, 'i')
		.replace(/[òóôõö]/g, 'o')
		.replace(/[ùúûü]/g, 'u')
		.replace(/[ç]/g, 'c')
		.replace(/[ñ]/g, 'n')
		.replace(/[^a-z0-9\s-]/g, '') // Remove special characters
		.replace(/\s+/g, '-') // Replace spaces with dashes
		.replace(/-+/g, '-') // Remove multiple dashes
		.replace(/^-|-$/g, ''); // Remove leading/trailing dashes
};

/**
 * Generate locale-aware job detail URL
 */
const generateJobUrl = (jobId: string, title: string | null | undefined, locale: string): string => {
	const slug = title ? generateSlug(title) : 'offre';

	// Map locale to URL path segments
	const pathSegments: { [key: string]: string } = {
		fr: 'trouver-un-emploi/offre',
		en: 'find-a-job/offer',
		es: 'encontrar-un-empleo/oferta',
	};

	const pathSegment = pathSegments[locale] || pathSegments.fr;
	return `/${pathSegment}/${jobId}/${slug}`;
};

/**
 * Returns the properties from the path of the URL.
 */
export interface PageParams extends PageBasicParams, ParsedUrlQuery {
	/**
     * Position parameter from URL
     */
	readonly position?: string;
	/**
     * Town parameter from URL
     */
	readonly town?: string;
	/**
     * Page number for pagination
     */
	readonly page?: string;
}

/**
 * Returns the properties from the search parameters of the URL.
 */
export interface PageQuery extends ParsedUrlQuery {
	/**
     * Additional search parameters
     */
	readonly [key: string]: string | string[] | undefined;
}

/**
 * Page props for jobs search page
 */
export interface PageProps extends PageBasicProps {
	readonly description: string;
	readonly footerData: FooterProps;
	readonly isOutsideScope?: boolean;
	readonly navbarData: NavbarProps;
	readonly originalPosition?: string;
	readonly originalTown?: string;
	readonly pageType: PAGE_TYPE;
	readonly pagination: {
		readonly currentPage: number;
		readonly totalPages: number;
		readonly totalItems: number;
		readonly itemsPerPage: number;
		readonly hasNextPage: boolean;
		readonly hasPrevPage: boolean;
	};
	readonly position: Recorded<Position>[];
	readonly publications: Recorded<Publication>[];
	readonly title: string;
	readonly town?: string;
}

export const defaultNavBarData: NavbarProps = {
	buttons: [
		{
			newTab: false,
			text: 'Se connecter',
			url: '/app/signin',
			variant: 'blackOnWhite',
		},
	],
	buttonsMobile: [
		{
			newTab: false,
			text: 'Se connecter',
			url: 'https://www.bruce.work/app/signin',
			variant: 'blackOnWhite',
		},
	],
	links: [
		{
			text: 'Trouver un emploi',
			url: '/trouver-un-emploi',
		},
		{
			text: 'Je recrute',
			url: '/recruter-du-personnel',
		},
	],
	linksMobile: [
		{
			id: 2,
			text: "Nos offres d'emploi",
			url: '/trouver-un-emploi',
			icon: {
				name: ICONS.Briefcase,
			},
			subLinks: [],
		},
		{
			id: 4,
			text: 'Je recrute',
			url: '/recruter-du-personnel',
			icon: {
				name: ICONS.Administration,
			},
			subLinks: [],
		},
		{
			id: 3,
			text: 'Notre Blog',
			url: 'https://blog.bruce.work/',
			icon: {
				name: ICONS.Pen,
			},
			subLinks: [],
		},
		{
			id: 1,
			text: 'Notre application mobile',
			url: '',
			icon: {
				name: ICONS.Phone,
			},
			subLinks: [],
		},
		{
			id: 5,
			text: 'Qui sommes-nous ?',
			url: '/notre-histoire',
			icon: {
				name: ICONS.Meeting,
			},
			subLinks: [],
		},
	],
	name: 'default',
};

export const defaultFooterData: FooterProps = {
	column: [
		{
			title: 'Talents',
			links: [
				{
					text: 'Application Talents',
					url: '/trouver-un-emploi/telecharger-application-talent',
				},
				{
					text: "Offres d'emploi",
					url: '/trouver-un-emploi',
				},
			],
		},
		{
			title: 'Entreprises',
			links: [
				{
					text: 'Recruter avec Bruce',
					url: '/recruter-du-personnel',
				},
				{
					text: "Qu'est ce que le recrutement digital",
					url: '/recruter-du-personnel',
				},
				{
					text: 'Grande entreprise',
					url: '/recruter-du-personnel',
				},
				{
					text: 'Start-up',
					url: '/recruter-du-personnel',
				},
				{
					text: 'PME',
					url: '/recruter-du-personnel',
				},
			],
		},
		{
			title: 'Ressources',
			links: [
				{
					text: 'Blog RH',
					url: 'https://blog.bruce.work/?blogCategory=pro',
				},
				{
					text: 'Blog Talents',
					url: 'https://blog.bruce.work/?blogCategory=talent',
				},
				{
					text: 'Livres blanc',
					url: '/livre',
				},
			],
		},
		{
			title: 'À propos',
			links: [
				{
					text: 'Bruce, notre histoire',
					url: '/notre-histoire',
				},
				{
					text: 'Presse',
					url: '/presse',
				},
				{
					text: 'Carrière',
					url: '/carriere',
				},
			],
		},
	],
	legalLinks: [
		{
			text: 'Politique de confidentialité',
			url: '/cgu',
		},
		{
			text: 'Mentions légales',
			url: '/cgu',
		},
		{
			text: 'Cookies',
			url: '/cgu',
		},
	],
	name: 'default',
};

// Interface for position option (moved from client-side to server-side data)
interface PositionOption {
	code: string
	text: string
	data: {
		className: string
		objectId: string
	}
}

const JobsPage = ({
	currentLang,
	currentPath,
	description,
	footerData,
	// eslint-disable-next-line @typescript-eslint/no-unused-vars -- dormant: used only by the commented-out UI below.
	isOutsideScope,
	navbarData,
	originalPosition,
	originalTown,
	pageType,
	pagination,
	position,
	publications,
	title,
	town,
}: PageProps) => {
	const { t } = useTranslation(['common', 'jobs']);
	const router = useRouter();

	// Search form state
	const [searchPosition, setSearchPosition] = useState('');
	// eslint-disable-next-line @typescript-eslint/no-unused-vars -- dormant: used only by the commented-out UI below.
	const [searchLocation, setSearchLocation] = useState('');
	// eslint-disable-next-line @typescript-eslint/no-unused-vars -- dormant: used only by the commented-out UI below.
	const [selectedLocation, setSelectedLocation] = useState<string>('');

	// Position autocomplete state - using server-side data
	// eslint-disable-next-line @typescript-eslint/no-unused-vars -- dormant: used only by the commented-out UI below.
	const [showPositionDropdown, setShowPositionDropdown] = useState(false);
	const [selectedPositionOption, setSelectedPositionOption] = useState<PositionOption | null>(null);

	// Convert server-side position data to PositionOption format for autocomplete
	const positionOptions: PositionOption[] = position
		.filter(pos => pos.name_fr) // Filter out positions without name_fr
		.map(pos => ({
			code: pos.objectId,
			text: pos.name_fr || '',
			data: {
				className: 'Position',
				objectId: pos.objectId,
			},
		}));

	// Handle position input change (using server-side data, no API calls)
	// eslint-disable-next-line @typescript-eslint/no-unused-vars -- dormant: used only by the commented-out UI below.
	const handlePositionInputChange = useCallback((value: string) => {
		setSearchPosition(value);
		setSelectedPositionOption(null);
		// Show dropdown if input has text and we have matching positions
		const hasMatches = value.length > 0 && positionOptions.some(option =>
			option.text.toLowerCase().includes(value.toLowerCase()),
		);
		setShowPositionDropdown(hasMatches);
	}, [positionOptions]);

	// Handle position option selection
	// eslint-disable-next-line @typescript-eslint/no-unused-vars -- dormant: used only by the commented-out UI below.
	const handlePositionSelect = useCallback((option: PositionOption) => {
		setSearchPosition(option.text);
		setSelectedPositionOption(option);
		setShowPositionDropdown(false);
	}, []);

	// Handle search form submission
	// eslint-disable-next-line @typescript-eslint/no-unused-vars -- dormant: used only by the commented-out UI below.
	const handleSearchSubmit = useCallback((e: React.FormEvent) => {
		e.preventDefault();

		let positionUrlSegment = '';
		let locationUrlSegment = '';

		if (searchPosition.trim()) {
			// If we have a selected position option, use its text, otherwise use the raw input
			const positionText = selectedPositionOption ? selectedPositionOption.text : searchPosition;
			const formattedPosition = positionText.toLowerCase()
				.split('/')[0]
				.trim()
				.replace(/\([^()]*\)/g, '')
				.replace(/ - /g, '-')
				.replace(/ /g, '-');
			positionUrlSegment = `/de-${formattedPosition}`;
		}

		if (selectedLocation.trim()) {
			const formattedLocation = selectedLocation.toLowerCase();
			locationUrlSegment = `/a-${formattedLocation}`;
		}

		if (positionUrlSegment || locationUrlSegment) {
			const newPath = `/${currentLang}/trouver-un-emploi/offres${positionUrlSegment}${locationUrlSegment}`;
			router.push(newPath);
		}
	}, [searchPosition, selectedLocation, router, currentLang, selectedPositionOption]);

	return (
		<PageWrapper
			currentLang={currentLang}
			currentPath={currentPath}
			description={description}
			footerData={footerData}
			introImageUrl="/images/job-search-default.jpg"
			isv2={true}
			navbarData={navbarData}
			pageType={pageType}
			title={title}
		>
			<VisualIntroSection background={'https://www.bruce.work/guests/collections/picture/result-banner.jpg'}>
				<VisualIntroOverlay />
				<VisualIntroContent>
					<Title_v2
						align={'center'}
						white
						padding='0 4rem 0 0'
						underlineColor={'red'}
					>
						{renderUnderline(parseUnderlineFromString(
							(position.length > 0 || (town))
								? t(
									'jobs:topBanner:titleQuery',
									{
										position: (position.length > 0 && position[0]?.name_fr) ? t('jobs:topBanner:titlePosition', { position: position[0].name_fr }) : '',
										town: (town) ? t('jobs:topBanner:titleInTown', { town }) : t('jobs:topBanner:titleInFrance'),
									})
								: t('jobs:topBanner:titleGeneric'),

						))}
					</Title_v2>

					{/* <VisualSearchForm>
                        <VisualSearchContainer>
                            <VisualAutocompleteWrapper>
                                <VisualSearchInput
                                    type="search"
                                    placeholder={t('jobs:topBanner.form.position.placeholder')}
                                    value={searchPosition}
                                    onChange={(e) => handlePositionInputChange(e.target.value)}
                                    onFocus={() => {
                                        if (positionOptions.length > 0) {
                                            setShowPositionDropdown(true)
                                        }
                                    }}
                                    onBlur={() => {
                                        // Delay hiding dropdown to allow click on options
                                        setTimeout(() => setShowPositionDropdown(false), 150)
                                    }}
                                />
                                {showPositionDropdown && positionOptions.length > 0 && (
                                    <VisualAutocompleteDropdown>
                                        {positionOptions
                                            .filter(option =>
                                                searchPosition.length === 0 ||
                                                option.text.toLowerCase().includes(searchPosition.toLowerCase())
                                            )
                                            .slice(0, 10)
                                            .map((option) => (
                                                <VisualAutocompleteOption
                                                    key={option.code}
                                                    onClick={() => handlePositionSelect(option)}
                                                >
                                                    {option.text}
                                                </VisualAutocompleteOption>
                                            ))}
                                    </VisualAutocompleteDropdown>
                                )}
                            </VisualAutocompleteWrapper>
                            <VisualCityInput
                                type="search"
                                placeholder={t('jobs:topBanner.form.city.placeholder')}
                                value={searchLocation}
                                onChange={(e) => {
                                    setSearchLocation(e.target.value)
                                    setSelectedLocation(e.target.value)
                                }}
                            />
                            <Button_v2
                                href=''
                                onClick={() => {
                                    const fakeEvent = { preventDefault: () => { } } as React.FormEvent;
                                    handleSearchSubmit(fakeEvent);
                                }}
                                text={t('jobs:topBanner:form:buttonText')}
                                version='whiteOnRed'
                            />
                        </VisualSearchContainer>
                    </VisualSearchForm> */}
				</VisualIntroContent>
			</VisualIntroSection>

			<VisualContainer>
				<VisualContainerTitle>

					<Title_v2
						underlineColor={'red'}
					>
						{renderUnderline(parseUnderlineFromString(t('jobs:results:title')))}
					</Title_v2>
					<VisualCountDisplay>
						<FiZap />
						<strong>{pagination.totalItems}</strong> {t('jobs:results:offersFound')}
					</VisualCountDisplay>
				</VisualContainerTitle>

				{/* <VisualHeader>
                    <VisualHeaderContent>
                        <VisualFiltersSection>
                            <Button_v2
                                href=''
                                onClick={() => { }}
                                margin={{ top: '1.5rem' }}
                                text={t('jobs:filters:refineSearch')}
                                version='whiteOnRed'
                            />
                            {isOutsideScope && (
                                <VisualInfoMessage>
                                    <FiMapPin />
                                    {t('jobs:filters:outOfScopeMessage')}
                                </VisualInfoMessage>
                            )}
                        </VisualFiltersSection>
                        <VisualSortSection>
                            <VisualSelectLabel>{t('jobs:filters:sortBy')}</VisualSelectLabel>
                            <VisualSelect>
                                <option value="date">{t('jobs:filters:sortOptions:date')}</option>
                                <option value="relevance">{t('jobs:filters:sortOptions:relevance')}</option>
                                <option value="company">{t('jobs:filters:sortOptions:company')}</option>
                            </VisualSelect>
                        </VisualSortSection>
                    </VisualHeaderContent>
                </VisualHeader> */}

				{publications.length > 0 ? (
					<>
						<VisualPublicationsList>
							{publications.map((publication, index) => (
								<VisualPublicationCard
									key={publication.objectId || index}
									publication={publication}
									positions={position}
									currentLang={currentLang}
								/>
							))}
						</VisualPublicationsList>

						{/* Pagination Controls */}
						<VisualPaginationContainer>
							<VisualPaginationInfo>
								{t('jobs:pagination:showing', {
									start: (pagination.currentPage - 1) * pagination.itemsPerPage + 1,
									end: Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems),
									total: pagination.totalItems,
								})}
							</VisualPaginationInfo>

							<VisualPaginationControls>
								{pagination.hasPrevPage && (
									<VisualPaginationButton
										href={generatePaginationUrl(pagination.currentPage - 1, currentLang, originalPosition, originalTown)}
										disabled={false}
									>
										{t('jobs:pagination:previous')}
									</VisualPaginationButton>
								)}

								{renderPaginationNumbers(pagination, currentLang, originalPosition, originalTown)}

								{pagination.hasNextPage && (
									<VisualPaginationButton
										href={generatePaginationUrl(pagination.currentPage + 1, currentLang, originalPosition, originalTown)}
										disabled={false}
									>
										{t('jobs:pagination:next')}
									</VisualPaginationButton>
								)}
							</VisualPaginationControls>
						</VisualPaginationContainer>
					</>
				) : (
					<VisualNoResults>
						<FiBriefcase />
						<h3>{t('jobs:noResults:title')}</h3>
						<p>{t('jobs:noResults:message')}</p>
					</VisualNoResults>
				)}
			</VisualContainer>
		</PageWrapper >
	);
};

// Pagination helper functions
const generatePaginationUrl = (page: number, currentLang: string, position?: string, town?: string): string => {
	let positionUrlSegment = '';
	let locationUrlSegment = '';

	if (position) {
		const formattedPosition = position.toLowerCase()
			.split('/')[0]
			.trim()
			.replace(/\([^()]*\)/g, '')
			.replace(/ - /g, '-')
			.replace(/ /g, '-');
		positionUrlSegment = `/de-${formattedPosition}`;
	}

	if (town) {
		const formattedLocation = town.toLowerCase();
		locationUrlSegment = `/a-${formattedLocation}`;
	}

	const baseUrl = `/${currentLang}/trouver-un-emploi/offres${positionUrlSegment}${locationUrlSegment}`;

	// Add page as query parameter if page > 1
	if (page > 1) {
		return `${baseUrl}?page=${page}`;
	}

	return baseUrl;
};

const renderPaginationNumbers = (pagination: PageProps['pagination'], currentLang: string, position?: string, town?: string) => {
	const numbers = [];
	const maxVisible = 5;
	let startPage = Math.max(1, pagination.currentPage - Math.floor(maxVisible / 2));
	const endPage = Math.min(pagination.totalPages, startPage + maxVisible - 1);

	// Adjust start if we're near the end
	if (endPage - startPage + 1 < maxVisible) {
		startPage = Math.max(1, endPage - maxVisible + 1);
	}

	// Add first page and ellipsis if needed
	if (startPage > 1) {
		numbers.push(
			<VisualPaginationButton
				key={1}
				href={generatePaginationUrl(1, currentLang, position, town)}
				active={1 === pagination.currentPage}
			>
				1
			</VisualPaginationButton>,
		);
		if (startPage > 2) {
			numbers.push(
				<VisualPaginationEllipsis key="ellipsis-start">
					...
				</VisualPaginationEllipsis>,
			);
		}
	}

	// Add page numbers
	for (let i = startPage; i <= endPage; i++) {
		numbers.push(
			<VisualPaginationButton
				key={i}
				href={generatePaginationUrl(i, currentLang, position, town)}
				active={i === pagination.currentPage}
			>
				{i}
			</VisualPaginationButton>,
		);
	}

	// Add ellipsis and last page if needed
	if (endPage < pagination.totalPages) {
		if (endPage < pagination.totalPages - 1) {
			numbers.push(
				<VisualPaginationEllipsis key="ellipsis-end">
					...
				</VisualPaginationEllipsis>,
			);
		}
		numbers.push(
			<VisualPaginationButton
				key={pagination.totalPages}
				href={generatePaginationUrl(pagination.totalPages, currentLang, position, town)}
				active={pagination.totalPages === pagination.currentPage}
			>
				{pagination.totalPages}
			</VisualPaginationButton>,
		);
	}

	return numbers;
};

// Visual Theme Card Components - Magazine-style job cards
const VisualCard = styled.article`
    background: ${({ theme }) => theme.color.white};
    border-radius: 8px;
    overflow: hidden;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    cursor: pointer;
    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
    border: 1px solid ${({ theme }) => theme.color.grey};
    
    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 30px rgba(0,0,0,0.12);
    }
`;

const VisualCardImageContainer = styled.div`
    position: relative;
    width: 100%;
    height: 200px;
    background: linear-gradient(135deg, ${({ theme }) => theme.color.prune} 0%, ${({ theme }) => theme.color.black} 100%);
    overflow: hidden;
`;

const VisualCardImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
    
    ${VisualCard}:hover & {
        transform: scale(1.05);
    }
`;

const VisualCardOverlay = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(transparent, rgba(31, 41, 55, 0.8));
    padding: 20px;
    color: ${({ theme }) => theme.color.white};
`;

const VisualCardContent = styled.div`
    padding: 24px;
`;

const VisualCardTitle = styled.h3`
    font-size: ${({ theme }) => theme.fontSize.LARGE};
    font-weight: 600;
    color: ${({ theme }) => theme.color.white};
    margin-bottom: 4px;
    line-height: 1.3;
    
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

const VisualCardMeta = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 16px;
`;

const VisualCardMetaItem = styled.div<{ variant?: 'light' | 'dark' }>`
    color: ${({ theme, variant }) => variant === 'dark' ? theme.color.black : theme.color.white};
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: ${({ theme }) => theme.fontSize.SMALL};
    
    span:first-child {
        font-size: 16px;
        min-width: 20px;
    }
`;

const VisualCardDescription = styled.p`
    font-size: ${({ theme }) => theme.fontSize.SMALL};
    line-height: 1.6;
    margin-bottom: 16px;
    
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

const VisualCardTags = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 20px;
`;

const VisualCardTag = styled.span<{ variant?: 'primary' | 'secondary' }>`
    padding: 6px 12px;
    border-radius: 4px;
    font-size: ${({ theme }) => theme.fontSize.XXSMALL};
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    
    ${({ variant, theme }) => {
		switch (variant) {
			case 'primary':
				return `
                    background: ${theme.color.black};
                    color: ${theme.color.white};
                `;
			case 'secondary':
				return `
                    background: ${theme.color.beige};
                    color: ${theme.color.white};
                `;
			default:
				return `
                    background: ${theme.color.greyLight};
                    border: 1px solid ${theme.color.grey};
                `;
		}
	}}
`;

const VisualPublicationCard = ({ publication, positions, currentLang }: {
	publication: Recorded<Publication>,
	positions?: Recorded<Position>[],
	currentLang: string,
}) => {
	const { t } = useTranslation(['common', 'jobs']);
	const indeedData = publication.targetData && 'indeed' in publication.targetData
		? publication.targetData.indeed
		: null;

	// Find matching position data for this publication
	const matchingPosition = positions?.find(pos => pos.objectId === publication.mission?.position?.objectId);

	const imageUrl = getJobImageUrl(publication, matchingPosition);

	const jobRemoteType = indeedData
		? (indeedData.remoteWork?.type === 'full')
			? t('jobs:card:remoteTypes:fullTime')
			: (indeedData.remoteWork?.type === 'hybrid')
				? t('jobs:card:remoteTypes:hybrid')
				: (indeedData.remoteWork?.type === 'none')
					? t('jobs:card:remoteTypes:none')
					: undefined
		: undefined;

	const addressLevel = indeedData?.addressLevel ?? 'state';

	const postalCode = (
		jobRemoteType
        &&
        indeedData
        &&
        indeedData.remoteWork?.type === 'full'
	)
		? ''
		: (['street', 'postalCode'].includes(addressLevel))
			? publication.address?.postalCode
			: null;

	const department = publication.address?.postalCode?.slice(0, 2) || null;

	// Get location from address or targetData
	const location = `${publication.address?.townName ?? ''} ${(postalCode || department) ? `(${postalCode ?? department})` : ''}`;

	// Get company name from targetData
	// const companyName = publication.targetData && 'indeed' in publication.targetData ?
	//     publication.targetData.indeed?.company :
	//     publication.organization?.name;

	// Job category belongs to the saved publication metadata.
	const category = publication.data?.facts?.category !== undefined
		? publication.data.facts.category : (publication.targetData && 'indeed' in publication.targetData)
			? publication.targetData.indeed?.jobType
			: undefined;

	const referenceDate = publication.publishedSince ?? publication.createdAt;

	if (!(publication.code)) {
		return null;
	}

	return (
		<VisualCard>
			{imageUrl ? (
				<VisualCardImageContainer>
					<VisualCardImage src={imageUrl} alt={publication.title ?? ''} />
					<VisualCardOverlay>
						<VisualCardTitle>
							{publication.title}
						</VisualCardTitle>
						{/* {companyName && (
                            <VisualCardMetaItem>
                                <span>🏢</span>
                                <span>{companyName}</span>
                            </VisualCardMetaItem>
                        )} */}
					</VisualCardOverlay>
				</VisualCardImageContainer>
			) : (
				<VisualCardImageContainer>
					<FiBriefcase />
					<VisualCardOverlay>
						<VisualCardTitle>
							{publication.title}
						</VisualCardTitle>
						{/* <VisualCardMetaItem>
                            <span>🏢</span>
                            <span>{companyName}</span>
                        </VisualCardMetaItem> */}
					</VisualCardOverlay>
				</VisualCardImageContainer>
			)}

			<VisualCardContent>
				<VisualCardMeta>
					{location && (
						<VisualCardMetaItem variant="dark">
							<FiMapPin />
							<span>{location}</span>
						</VisualCardMetaItem>
					)}
					<VisualCardMetaItem variant="dark">
						<FiCalendar />
						<span>{referenceDate ? formatTimeAgo(referenceDate, t) : t('jobs:card:recently')}</span>
					</VisualCardMetaItem>
				</VisualCardMeta>

				<VisualCardTags>
					{category && (
						<VisualCardTag variant="primary">
							{category}
						</VisualCardTag>
					)}
					<VisualCardTag variant="secondary">
						{t('jobs:card:contractTypes:fullTime')}
					</VisualCardTag>
					{jobRemoteType && (
						<VisualCardTag>
							{jobRemoteType}
						</VisualCardTag>
					)}
				</VisualCardTags>

				{publication.body && (
					<VisualCardDescription>
						{cleanHtmlToText(publication.body, 200)}
					</VisualCardDescription>
				)}
				<Button_v2
					href={generateJobUrl(publication.code, publication.title, currentLang)}
					onClick={() => { }}
					margin={{ top: '1.5rem' }}
					text={t('jobs:card:viewDetails')}
					version='whiteOnRed'
				/>
			</VisualCardContent>
		</VisualCard >
	);
};

// Query Functions
const queryForPublications = (position?: string, town?: string, page: number = 1, itemsPerPage: number = 20): Piece<Publication> => {
	const whereClause: Editable<Piece<Publication>['data']['where']['field']> = {
		campaign: {
			'∉': [
				{ objectId: 'o4uv63VIk7', className: 'Campaign' },
				{ objectId: 'Ar1W0C3I8n', className: 'Campaign' },
				{ objectId: 'hDWGfWrVqj', className: 'Campaign' },
			],
		},
		tags: { '∌': 'manual' },
		target: { '=': PublicationTarget.INDEED },
		status: {
			'∈': [PublicationStatus.ACTIVE, PublicationStatus.PENDING, PublicationStatus.UNSYNCHRONIZED],
		},
	};
	let input: Editable<Piece<Publication>['data']['input']> | undefined;
	if (position) {
		input = { field: { title: {} }, value: position };
	}
	if (town) {
		input = { field: { ...input?.field, address: {} }, value: (input ? input.value + ' ' : '') + town };
	}

	const start = (page - 1) * itemsPerPage;

	return {
		data: {
			class: {
				fields: [
					'address',
					'body',
					'code',
					'createdAt',
					'mission.feature',
					'mission.job.image',
					'organization.name',
					'publishedSince',
					'targetData',
					'data',
					'title',
				],
				name: 'Publication',
			},
			input,
			where: {
				field: whereClause,
				limit: itemsPerPage,
				order: ['-', 'publishedSince'],
				start,
			},
		},
	};
};

// Query function to get total count of publications
const queryForPublicationsCount = (position?: string, town?: string): Piece<Publication> => {
	const whereClause: Editable<Piece<Publication>['data']['where']['field']> = {
		campaign: {
			'∉': [
				{ objectId: 'o4uv63VIk7', className: 'Campaign' },
				{ objectId: 'Ar1W0C3I8n', className: 'Campaign' },
				{ objectId: 'hDWGfWrVqj', className: 'Campaign' },
			],
		},
		tags: { '∌': 'manual' },
		target: { '=': PublicationTarget.INDEED },
		status: {
			'∈': [PublicationStatus.ACTIVE, PublicationStatus.PENDING, PublicationStatus.UNSYNCHRONIZED],
		},
	};
	let input: Editable<Piece<Publication>['data']['input']> | undefined;
	if (position) {
		input = { field: { title: {} }, value: position };
	}
	if (town) {
		input = { field: { ...input?.field, address: {} }, value: (input ? input.value + ' ' : '') + town };
	}

	return {
		data: {
			class: {
				fields: [],
				name: 'Publication',
			},
			input,
			where: {
				field: whereClause,
				limit: 1000,
				order: [],
				start: 0,
			},
		},
	};
};

// Visual Theme Styled Components - Magazine-style layout with elegant typography

const VisualIntroSection = styled.section<{ background: string }>`
    position: relative;
    height: 500px;
    background: linear-gradient(rgba(31, 41, 55, 0.4), rgba(31, 41, 55, 0.6)), url(${props => props.background});
    background-size: cover;
    background-position: center;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    overflow: hidden;
    
    @media ${devices.mobile} {
        height: 400px;
    }
`;

const VisualIntroOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(31, 41, 55, 0.8) 0%, rgba(217, 119, 6, 0.1) 100%);
`;

const VisualIntroContent = styled.div`
    position: relative;
    z-index: 2;
    text-align: center;
    margin: 0 auto;
    padding: 0 20px;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- dormant: leftover of that commented-out UI.
const VisualTitle = styled.h1`
    font-size: ${({ theme }) => theme.fontSize.XHEADLINE};
    font-weight: 700;
    line-height: 1.2;
    margin-bottom: 20px;
    /* text-shadow: 2px 2px 4px rgba(0,0,0,0.3); */
    
    @media ${devices.mobile} {
        font-size: ${({ theme }) => theme.fontSize.XXLARGE};
    }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- dormant: used only by the commented-out UI below.
const VisualSearchForm = styled.form`
    margin-top: 40px;
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;
    
    @media ${devices.mobile} {
        margin-top: 30px;
        padding: 0 10px;
    }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- dormant: used only by the commented-out UI below.
const VisualSearchContainer = styled.div`
    display: flex;
    background: rgba(255, 255, 255, 0.95);
    border-radius: 8px;
    padding: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    backdrop-filter: blur(10px);
    
    @media ${devices.mobile} {
        flex-direction: column;
        gap: 10px;
    }
`;

const VisualSearchInput = styled.input`
    flex: 1;
    border: none;
    padding: 16px 20px;
    font-size: ${({ theme }) => theme.fontSize.SMALL};
    border-radius: 4px;
    width: 100%;
    
    &:focus {
        outline: none;
        background: ${({ theme }) => theme.color.beigeLight};
    }
    
    &::placeholder {
        color: ${({ theme }) => theme.color.blackLight};
    }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- dormant: used only by the commented-out UI below.
const VisualCityInput = styled(VisualSearchInput)`
    margin-right: 8px;
    
    @media ${devices.mobile} {
        margin-right: 0;
        margin-bottom: 10px;
    }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- dormant: used only by the commented-out UI below.
const VisualAutocompleteWrapper = styled.div`
    position: relative;
    flex: 1;
    margin-right: 8px;
    
    @media ${devices.mobile} {
        margin-right: 0;
        margin-bottom: 10px;
    }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- dormant: used only by the commented-out UI below.
const VisualAutocompleteDropdown = styled.div`
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: ${({ theme }) => theme.color.white};
    border: 1px solid ${({ theme }) => theme.color.grey};
    border-radius: 0 0 4px 4px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    max-height: 300px;
    overflow-y: auto;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- dormant: used only by the commented-out UI below.
const VisualAutocompleteOption = styled.div`
    color: ${({ theme }) => theme.color.black};
    padding: 12px 16px;
    cursor: pointer;
    font-size: ${({ theme }) => theme.fontSize.SMALL};
    border-bottom: 1px solid ${({ theme }) => theme.color.greyLight};
    
    &:hover {
        background: ${({ theme }) => theme.color.greyLight};
    }
    
    &:last-child {
        border-bottom: none;
    }
`;

const VisualContainer = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 60px 20px;
    
    @media ${devices.mobile} {
        padding: 40px 20px;
    }
`;

const VisualContainerTitle = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 40px;
`;

const VisualCountDisplay = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: ${({ theme }) => theme.fontSize.LARGE};
    color: ${({ theme }) => theme.color.blackLight};
    
    svg {
        color: ${({ theme }) => theme.color.red};
        font-size: 18px;
    }
    
    strong {
        color: ${({ theme }) => theme.color.black};
        font-weight: 600;
        margin: 0 4px;
    }
    
    @media ${devices.mobile} {
        padding: 12px 16px;
        margin-bottom: 20px;
    }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- dormant: used only by the commented-out UI below.
const VisualHeader = styled.div`
    margin-bottom: 50px;
    padding: 30px 40px;
    background: ${({ theme }) => theme.color.white};
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
    border-top: 4px solid ${({ theme }) => theme.color.beige};
    
    @media ${devices.mobile} {
        padding: 20px;
        margin-bottom: 30px;
    }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- dormant: used only by the commented-out UI below.
const VisualHeaderContent = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 20px;
    
    @media ${devices.mobile} {
        flex-direction: column;
        align-items: stretch;
    }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- dormant: used only by the commented-out UI below.
const VisualFiltersSection = styled.div`
    display: flex;
    align-items: center;
    gap: 20px;
    flex-wrap: wrap;
    
    @media ${devices.mobile} {
        flex-direction: column;
        align-items: stretch;
        gap: 15px;
    }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- dormant: leftover of that commented-out UI.
const VisualFilterButton = styled.button`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 24px;
    background: ${({ theme }) => theme.color.black};
    color: ${({ theme }) => theme.color.white};
    border: none;
    border-radius: 4px;
    font-size: ${({ theme }) => theme.fontSize.SMALL};
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    
    &:hover {
        background: ${({ theme }) => theme.color.beige};
        transform: translateY(-1px);
        box-shadow: 0 6px 20px rgba(217, 119, 6, 0.15);
    }
    
    span {
        font-size: 16px;
    }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- dormant: used only by the commented-out UI below.
const VisualInfoMessage = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 20px;
    border-radius: 4px;
    border-left: 4px solid ${({ theme }) => theme.color.beige};
    font-size: ${({ theme }) => theme.fontSize.SMALL};
    
    span {
        font-size: 16px;
    }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- dormant: used only by the commented-out UI below.
const VisualSortSection = styled.div`
    display: flex;
    align-items: center;
    gap: 15px;
    
    @media ${devices.mobile} {
        justify-content: space-between;
    }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- dormant: used only by the commented-out UI below.
const VisualSelectLabel = styled.label`
    font-size: ${({ theme }) => theme.fontSize.SMALL};
    font-weight: 500;
    color: ${({ theme }) => theme.color.black};
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- dormant: used only by the commented-out UI below.
const VisualSelect = styled.select`
    padding: 10px 16px;
    border: 2px solid ${({ theme }) => theme.color.grey};
    border-radius: 4px;
    background: white;
    font-size: ${({ theme }) => theme.fontSize.SMALL};
    color: ${({ theme }) => theme.color.black};
    cursor: pointer;
    min-width: 180px;
    
    &:focus {
        outline: none;
        border-color: ${({ theme }) => theme.color.beige};
        box-shadow: 0 0 0 3px rgba(217, 119, 6, 0.1);
    }
    
    &:hover {
        border-color: ${({ theme }) => theme.color.beige};
    }
`;

const VisualPublicationsList = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
    gap: 30px;
    
    @media ${devices.mobile} {
        grid-template-columns: 1fr;
        gap: 20px;
    }
`;

const VisualNoResults = styled.div`
    text-align: center;
    padding: 80px 20px;
    
    span {
        font-size: 64px;
        display: block;
        margin-bottom: 20px;
    }
    
    h3 {
        font-size: ${({ theme }) => theme.fontSize.XXLARGE};
        font-weight: 600;
        color: ${({ theme }) => theme.color.black};
        margin-bottom: 15px;
    }
    
    p {
        font-size: ${({ theme }) => theme.fontSize.LARGE};
        line-height: 1.6;
        max-width: 500px;
        margin: 0 auto;
    }
`;

// Pagination styled components
const VisualPaginationContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 30px;
    margin-top: 60px;
    padding-top: 40px;
    border-top: 1px solid ${({ theme }) => theme.color.greyLight};
    
    @media ${devices.mobile} {
        margin-top: 40px;
        padding-top: 30px;
        gap: 20px;
    }
`;

const VisualPaginationInfo = styled.div`
    font-size: ${({ theme }) => theme.fontSize.SMALL};
    color: ${({ theme }) => theme.color.blackLight};
    text-align: center;
`;

const VisualPaginationControls = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    justify-content: center;
    
    @media ${devices.mobile} {
        gap: 8px;
    }
`;

const VisualPaginationButton = styled.a<{ active?: boolean; disabled?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 44px;
    height: 44px;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: ${({ theme }) => theme.fontSize.SMALL};
    font-weight: 500;
    text-decoration: none;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    cursor: pointer;
    
    ${({ active, theme }) => active ? `
        background: ${theme.color.beige};
        color: ${theme.color.white};
        box-shadow: 0 2px 8px rgba(217, 119, 6, 0.2);
    ` : `
        background: ${theme.color.white};
        color: ${theme.color.black};
        border: 1px solid ${theme.color.grey};
        
        &:hover {
            background: ${theme.color.greyLight};
            border-color: ${theme.color.beige};
            transform: translateY(-1px);
        }
    `}
    
    ${({ disabled, theme }) => disabled && `
        opacity: 0.5;
        cursor: not-allowed;
        
        &:hover {
            transform: none;
            background: ${theme.color.greyLight};
        }
    `}
    
    @media ${devices.mobile} {
        min-width: 36px;
        height: 36px;
        font-size: ${({ theme }) => theme.fontSize.XSMALL};
    }
`;

const VisualPaginationEllipsis = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 44px;
    height: 44px;
    font-size: ${({ theme }) => theme.fontSize.SMALL};
    color: ${({ theme }) => theme.color.blackLight};
    
    @media ${devices.mobile} {
        min-width: 36px;
        height: 36px;
    }
`;

export const getServerSideProps: GetServerSideProps<PageProps, PageParams, PageQuery> = async (context) => {
	const { locale = 'fr', query, req } = context;
	const { position: queryPosition, town: queryTown, page: queryPage, language: queryLanguage } = query as PageParams & PageQuery;

	// Use language from query parameter if provided, otherwise fall back to locale
	const currentLang = (queryLanguage as string) || locale;

	// Parse position and town parameters correctly based on their prefixes
	let position: string | undefined;
	let town: string | undefined;

	if (queryPosition && queryPosition !== '') {
		if (queryPosition.startsWith('de-')) {
			position = queryPosition.substring(3);
		} else if (queryPosition.startsWith('a-')) {
			// If position parameter starts with 'a-', it's actually a town
			town = queryPosition.substring(2);
		} else {
			position = queryPosition;
		}
	}

	if (queryTown && queryTown !== '') {
		if (queryTown.startsWith('a-')) {
			town = queryTown.substring(2);
		} else if (queryTown.startsWith('de-')) {
			// If town parameter starts with 'de-', it's actually a position
			position = queryTown.substring(3);
		} else {
			town = queryTown;
		}
	}

	// Parse pagination parameters - handle both string and array cases
	let currentPage = 1;
	if (queryPage) {
		const pageValue = Array.isArray(queryPage) ? queryPage[0] : queryPage;
		const parsedPage = parseInt(pageValue as string, 10);
		if (!isNaN(parsedPage) && parsedPage > 0) {
			currentPage = parsedPage;
		}
	}

	const itemsPerPage = 20;

	try {
		// Convert position parameter
		const normalizedPosition = position === 'libre-service' ? 'employe-libre-service' : position;

		// Always get the total count first for accurate pagination
		let totalCount = 0;
		let isOutsideScope = false;

		try {
			const countQuery = await startServerSide.query<Publication>(queryForPublicationsCount(normalizedPosition, town));
			totalCount = countQuery?.length || 0;

			// If no results with specific filters, try getting all publications count
			if (totalCount === 0 && (normalizedPosition || town)) {
				const allCountQuery = await startServerSide.query<Publication>(queryForPublicationsCount());
				totalCount = allCountQuery?.length || 0;
				isOutsideScope = true;
			}
		} catch (error) {
			console.error('Error getting publication count:', error);
			// Fallback: try to get all publications count
			try {
				const allCountQuery = await startServerSide.query<Publication>(queryForPublicationsCount());
				totalCount = allCountQuery?.length || 0;
				isOutsideScope = true;
			} catch (fallbackError) {
				console.error('Error getting fallback publication count:', fallbackError);
				totalCount = 0;
			}
		}

		// Get publications data using startServerSide with pagination
		let publications: Recorded<Publication>[] = [];
		if (totalCount > 0) {
			try {
				if (isOutsideScope) {
					publications = await startServerSide.query<Publication>(queryForPublications(undefined, undefined, currentPage, itemsPerPage));
				} else {
					publications = await startServerSide.query<Publication>(queryForPublications(normalizedPosition, town, currentPage, itemsPerPage));
				}
			} catch (error) {
				console.error('Error getting publications:', error);
				publications = [];
			}
		}

		// Calculate pagination data
		const totalPages = Math.ceil(totalCount / itemsPerPage);
		const pagination = {
			currentPage,
			totalPages,
			totalItems: totalCount,
			itemsPerPage,
			hasNextPage: currentPage < totalPages,
			hasPrevPage: currentPage > 1,
		};

		// // Fetch all positions for autocomplete functionality
		// const queryForPositions = (
		//     searchedText: string = '',
		//     queryField: Piece<Position>['data']['where']['field'] = {}
		// ): Piece<Position> => {
		//     let pieceOfPosition: Piece<Position> = {
		//         data: {
		//             class: {
		//                 fields: ['name_fr', 'shortName_fr', 'job.name_fr', 'name_fr_m'],
		//                 name: 'Position',
		//             },
		//             where: {
		//                 field: queryField,
		//                 limit: 100,
		//                 order: [],
		//                 start: 0,
		//             },
		//         },
		//     }

		//     if (searchedText) {
		//         pieceOfPosition = {
		//             data: {
		//                 ...pieceOfPosition.data,
		//                 input: {
		//                     field: {
		//                         name_fr: {},
		//                     },
		//                     value: searchedText,
		//                 },
		//             },
		//         }
		//     }

		//     return pieceOfPosition
		// }

		// // Get all positions for autocomplete
		// const allPositions = await startServerSide.query<Position>(queryForPositions())

		// Get position name and create title/description
		const positionTag = position || undefined;
		const positionName = getPositionName(positionTag);
		const positionData: any[] = positionName
			? [{ name_fr: positionName }]
			: positionTag
				? [{ name_fr: formatPositionName(positionTag) }]
				: [];

		// Process town data
		const townFormatted = town
			? town.split('-(')[0].toLowerCase().replace(/[-]/g, ' ').replace(/(^|[^A-Za-zÀ-ÖØ-öø-ÿ])((?!(des|sur|les|aux|par)[^A-Za-zÀ-ÖØ-öø-ÿ])(\w)(?!\w\s))/g, str => str.toUpperCase())
			: undefined;

		// Generate meta title and description
		const title = positionData.length > 0 && positionData[0]?.name_fr
			? `Jobs for ${positionData[0].name_fr}${townFormatted ? ` in ${townFormatted}` : ''}`
			: `Job Search${townFormatted ? ` in ${townFormatted}` : ''}`;

		const description = positionData.length > 0 && positionData[0]?.name_fr
			? `Find ${positionData[0].name_fr} jobs${townFormatted ? ` in ${townFormatted}` : ''}. Browse available positions and apply today.`
			: `Search for jobs${townFormatted ? ` in ${townFormatted}` : ''}. Find your next career opportunity.`;

		// Fetch localized navbar and footer data from CMS
		const [navbarData, footerData] = await Promise.all([
			fetchNavbarData(currentLang),
			fetchFooterData(currentLang),
		]);

		return {
			props: {
				...(await serverSideTranslations(currentLang, ['common', 'jobs'], nextI18NextConfig)),
				publications,
				position: positionData,
				town: townFormatted,
				isOutsideScope,
				pageType: PAGE_TYPE.b2c,
				currentPath: req.url || '',
				currentLang,
				title,
				description,
				pagination,
				originalPosition: position,
				originalTown: town,
				navbarData: navbarData || defaultNavBarData,
				footerData: footerData || defaultFooterData,
			},
		};
	} catch (error) {
		console.error('Error in getServerSideProps:', error);

		// Fetch localized navbar and footer data from CMS even in error case
		const [navbarData, footerData] = await Promise.all([
			fetchNavbarData(locale),
			fetchFooterData(locale),
		]);

		return {
			props: {
				...(await serverSideTranslations(locale, ['common', 'jobs'], nextI18NextConfig)),
				publications: [],
				position: [],
				pageType: PAGE_TYPE.b2c,
				currentPath: context.req.url || '',
				currentLang: locale,
				title: 'Job Search',
				description: 'Search for job opportunities',
				pagination: {
					currentPage: 1,
					totalPages: 1,
					totalItems: 0,
					itemsPerPage: 20,
					hasNextPage: false,
					hasPrevPage: false,
				},
				originalPosition: undefined,
				originalTown: undefined,
				navbarData: navbarData || defaultNavBarData,
				footerData: footerData || defaultFooterData,
			},
		};
	}
};

// Helper function to get position name from the large mapping
const getPositionName = (positionTag: string | undefined): string | undefined => {
	if (!(positionTag)) {
		return undefined;
	}

	const positionNames: Record<string, string> = {
		'receptionniste': 'Réceptionniste',
		'adjoint-administratif': 'Adjoint Administratif',
		'charge-de-recouvrement': 'Chargé de Recouvrement',
		'conseiller-en-gestion-de-patrimoine': 'Conseiller en Gestion de Patrimoine',
		'responsable-centre-de-profit': 'Responsable Centre de Profit',
		'analyste-risques': 'Analyste Risques',
		'agent-de-recouvrement': 'Agent de Recouvrement',
		'ouvrier-agricole': 'Ouvrier Agricole',
		'agroalimentaire': 'Agroalimentaire',
		'paysagiste': 'Paysagiste',
		'dessinateur-projeteur': 'Dessinateur-projeteur',
		'dessinateur-autocad': 'Dessinateur AutoCAD',
		'projeteur': 'Projeteur',
		'charge-d-edition': 'Chargé d\'édition',
		'graphiste': 'Graphiste',
		'monteur': 'Monteur',
		'dessinateur': 'Dessinateur',
		'assistant-de-laboratoire': 'Assistant de Laboratoire',
		'technicien-d-analyse-en-biologie-medicale': 'Technicien d\'analyse en Biologie Médicale',
		'assistant-technique-en-pharmacie': 'Assistant Technique en Pharmacie',
		'gestionnaire-assurance': 'Gestionnaire Assurance',
		'agent-d-assurance': 'Agent d\'assurance',
		'technicien-automobile': 'Technicien Automobile',
		'agent-de-centre-d-appel': 'Agent de Centre d\'appel',
		'conseiller-de-vente': 'Conseiller de Vente',
		'commercial': 'Commercial',
		'conseiller-commercial': 'Conseiller Commercial',
		'charge-d-affaires': 'Chargé d\'affaires',
		'commercial-e-commerce': 'Commercial E-commerce',
		'vendeur-conseil': 'Vendeur Conseil',
		'commercial-terrain': 'Commercial Terrain',
		'chef-de-secteur': 'Chef de Secteur',
		'comptable-clients-fournisseurs': 'Comptable Clients Fournisseurs',
		'responsable-comptable': 'Responsable Comptable',
		'ouvrier-polyvalent-du-batiment': 'Ouvrier Polyvalent du Bâtiment',
		'electrotechnicien': 'Électrotechnicien',
		'electricien-du-batiment': 'Électricien du Bâtiment',
		'poseur': 'Poseur',
		'peintre-batiment': 'Peintre Bâtiment',
		'developpeur': 'Développeur',
		'chef-de-projet-digital': 'Chef de Projet Digital',
		'ingenieur-developpement-php': 'Ingénieur Développement PHP',
		'developpeur-front-end': 'Développeur Front End',
		'developpeur-java': 'Développeur Java',
		'ingenieur-test-et-validation': 'Ingénieur Test et Validation',
		'assistant-de-gestion': 'Assistant de Gestion',
		'consultant': 'Consultant',
		'gerant': 'Gérant',
		'chef-d-equipe': 'Chef d\'équipe',
		'assistant-manager': 'Assistant Manager',
		'responsable-production': 'Responsable Production',
		'directeur-adjoint': 'Directeur Adjoint',
		'responsable-adjoint': 'Responsable Adjoint',
		'responsable-supply-chain': 'Responsable Supply Chain',
		'employe-libre-service': 'Employé Libre Service',
		// Add more mappings as needed - this is a truncated version for brevity
	};

	return positionNames[positionTag];
};

const formatPositionName = (positionTag: string): string => {
	return positionTag
		.toLowerCase()
		.replace(/[-]/g, ' ')
		.replace(/(^|[^A-Za-zÀ-ÖØ-öø-ÿ])((?!(des|sur|les|aux|par)[^A-Za-zÀ-ÖØ-öø-ÿ])(\w)(?!\w\s))/g, str => str.toUpperCase());
};

export default JobsPage;
